import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../api";
import Topbar from "../Topbar";
import ContactDetailPage from "../ContactDetailPage";
import useEscapeLayer from "../useEscape";
import useIsMobile from "../../hooks/useIsMobile";
import LivePanel from "./LivePanel";
import Popover from "../datepicker/Popover";
import RangePanel from "../datepicker/RangePanel";
import { CalendarIcon } from "../datepicker/Months";
import { rangePresets } from "../datepicker/calendar.mjs";
import { Country, PropDot, SourceCell, StageChip } from "./parts";
import {
  PRESETS,
  COUNTRY_FROM,
  avgTime,
  chartModel,
  campaignGroups,
  campaignSummary,
  convRate,
  clock,
  countryRows,
  countrySummary,
  customRange,
  dayKey,
  kpis,
  logRows,
  pageRows,
  personLine,
  presetRange,
  presetTitle,
  rangeLabel,
  secondsSince,
  sourceRows,
  sourceView,
  spendNote,
  stageChip,
  trackedLine,
  usd,
  variantNote,
  whenLabel,
  whereLabel,
  didWords,
} from "./analyticsModel.mjs";
import "./analytics.css";

// The day the beats began (PR #243): before it no visit was timed.
const MEASURED_FROM = "18 Oct";
const LOG_STEP = 12; // the canvas shows ten or so; "Older visits" adds more
const TABS = [
  ["overview", "Overview"],
  ["breakdowns", "Breakdowns"],
];
const BREAKDOWNS = [
  ["country", "Country"],
  ["campaign", "Ad campaign"],
];

/*
 * INBOUND ▸ ANALYTICS: remotion.io, the landing pages and the booking page
 * together. Two jobs, in his order: who is on right now (LivePanel), then how
 * a date range went: the five numbers, visits per day, each property, where
 * visitors came from, remotion.io's pages, and who came. A known person's name
 * anywhere opens the side panel; from there, the full record.
 *
 * Built from his "CRM Analytics" canvas, 2026-10-18. Data: src/analytics.
 *
 * BREAKDOWNS (?tab=breakdowns, asked for 2026-10-21) cuts the same range by one
 * thing at a time (?by=country|campaign). It reads the same overview, so the
 * range carries across the two tabs.
 */
export default function AnalyticsPage() {
  const mobile = useIsMobile();
  const [preset, setPreset] = useState("30");
  const [range, setRange] = useState(() => presetRange("30"));
  const [title, setTitle] = useState(null); // a ready-made range's name, e.g. "Last month"
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [person, setPerson] = useState(null); // { person, entry? }
  const [full, setFull] = useState(null); // contact id with the full record open
  const [live, setLive] = useState([]);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "breakdowns" ? "breakdowns" : "overview";
  const by = params.get("by") === "campaign" ? "campaign" : "country";

  useEffect(() => {
    let gone = false;
    setError(null);
    api(`/analytics/overview?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`)
      .then((d) => !gone && setData(d))
      .catch((err) => !gone && setError(err.message));
    return () => {
      gone = true;
    };
  }, [range]);

  const pick = useCallback((key) => {
    setPreset(key);
    setRange(presetRange(key));
  }, []);

  const openPerson = useCallback((p, entry = null) => {
    setFull(null);
    setPerson({ person: p, entry });
  }, []);
  const close = useCallback(() => {
    setPerson(null);
    setFull(null);
  }, []);
  useEscapeLayer(!!person || !!full, close);

  return (
    <>
      <Topbar title="Analytics" sub="remotion.io · Landing pages · Booking page" subMobile="Inbound">
        <span className="an-faint an-zone">Israel time</span>
      </Topbar>
      <div className="an-page">
        <div className="crm-auto-tabs an-tabs" role="tablist">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={`crm-auto-tab${tab === key ? " is-active" : ""}`}
              onClick={() => setParams(key === "overview" ? {} : { tab: key }, { replace: true })}
            >
              {label}
            </button>
          ))}
        </div>
        {tab === "overview" && <LivePanel onOpen={openPerson} onLive={setLive} />}

        <RangeHeader preset={preset} title={title} range={range} pick={pick} setCustom={(r, label) => {
            setPreset("custom");
            setTitle(label);
            setRange(r);
          }} mobile={mobile} />
        {data && trackedLine(data.tracked_from) && (
          <p className="an-tracked">
            {mobile ? "Tracked from: " : "What we have: "}
            {trackedLine(data.tracked_from)}.{mobile ? "" : " Days before that show as not tracked, never as zero."}
          </p>
        )}
        {error && <p className="an-error">Could not read the analytics: {error}</p>}
        {!data && !error && <p className="an-muted">Reading the visits…</p>}

        {tab === "breakdowns" && (
          <div role="group" aria-label="Break down by" className="an-seg an-seg--sm an-byseg">
            {BREAKDOWNS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={by === key}
                className={by === key ? "is-on" : ""}
                onClick={() => setParams(key === "country" ? { tab: "breakdowns" } : { tab: "breakdowns", by: key }, { replace: true })}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {data && tab === "breakdowns" && by === "country" && <Countries data={data} mobile={mobile} />}
        {data && tab === "breakdowns" && by === "campaign" && <Campaigns data={data} mobile={mobile} />}
        {data && tab === "overview" && (
          <>
            <Kpis data={data} mobile={mobile} />
            <div className="an-row an-row--chart">
              <VisitsChart data={data} mobile={mobile} />
              <Properties data={data} mobile={mobile} />
            </div>
            <div className="an-row an-row--bottom">
              <div className="an-stack">
                <Sources data={data} mobile={mobile} />
                <Pages data={data} mobile={mobile} />
              </div>
              <WhoCame range={range} preset={preset} onOpen={openPerson} mobile={mobile} />
            </div>
          </>
        )}
      </div>

      {person && !full && <PersonPanel person={person.person} live={live} onClose={close} onFull={() => setFull(person.person.contact_id)} />}
      {full && (
        <aside className="an-fullrecord" aria-label="Contact record">
          <ContactDetailPage key={full} contactId={full} embedded onClose={close} hostLabel="Analytics" />
        </aside>
      )}
    </>
  );
}

/* ---------------------------------------------------------------- range -- */

function RangeHeader({ preset, title, range, pick, setCustom, mobile }) {
  const [open, setOpen] = useState(false);
  const btn = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  const today = dayKey(new Date());
  // The page's own four are rolling (to this minute), so they keep their own
  // meaning here; the rest are whole calendar days.
  const presets = [
    ...PRESETS.slice(0, 1).map((p) => ({ key: p.key, label: p.label, onSelect: () => (pick(p.key), close()) })),
    ...rangePresets(["yesterday"], today),
    ...PRESETS.slice(1).map((p) => ({ key: p.key, label: `Last ${p.label}`, onSelect: () => (pick(p.key), close()) })),
    ...rangePresets(["thisWeek", "lastWeek", "thisMonth", "lastMonth"], today),
  ];
  const apply = ({ from, to }, p) => {
    const r = customRange(from, to);
    if (r) {
      setCustom(r, p ? p.label : null);
      close();
    }
  };
  return (
    <div className="an-rangehead">
      <div>
        <p className="an-eyebrow">Over a date range</p>
        <div className="an-rangehead__title">
          <h2>{preset === "custom" && title ? title : presetTitle(preset, range)}</h2>
          {!mobile && <span className="an-muted">{rangeLabel(range)}</span>}
        </div>
      </div>
      <div className="an-rangehead__controls">
        {!mobile && (
          <div role="group" aria-label="Date range" className="an-seg">
            {PRESETS.map((p) => (
              <button key={p.key} type="button" aria-pressed={preset === p.key} className={preset === p.key ? "is-on" : ""} onClick={() => pick(p.key)}>
                {p.label}
              </button>
            ))}
          </div>
        )}
        <button ref={btn} type="button" className={`an-datebtn${preset === "custom" ? " is-on" : ""}`} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          {!mobile && <CalendarIcon />}
          {rangeLabel(range)}
          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
        {open && (
          <Popover anchor={btn} onClose={close} align="right" label="Pick dates">
            <RangePanel
              from={dayKey(range.from)}
              to={dayKey(new Date(new Date(range.to).getTime() - 1))}
              max={today}
              presets={presets}
              activeKey={preset === "custom" ? (presets.find((p) => p.label === title) || {}).key : preset}
              onApply={apply}
              onCancel={close}
              applyLabel="Show these days"
              mobile={mobile}
            />
          </Popover>
        )}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- kpis -- */

function Kpis({ data, mobile }) {
  const list = kpis(data);
  return (
    <div className={`an-kpis${mobile ? " is-phone" : ""}`}>
      {list.map((k) => (
        <div key={k.key} className={`an-card an-kpi an-kpi--${k.key}`}>
          <span className="an-kpi__label">
            {!mobile && <span aria-hidden="true" className={`an-kpi__mark an-kpi__mark--${k.key}`} />}
            {k.label}
          </span>
          <span className="an-kpi__value">{k.value}</span>
          <span className="an-kpi__sub">{mobile ? k.phoneSub : k.sub}</span>
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------- chart -- */

function VisitsChart({ data, mobile }) {
  const model = useMemo(() => {
    const m = chartModel(data.days, data.tracked_from);
    if (!mobile) return m;
    // The phone starts at the first tracked day: a third of a 390px chart
    // saying "not tracked" is room the bars need.
    const first = m.cols.findIndex((c) => c.tracked);
    if (first <= 0) return m;
    const days = data.days.slice(first);
    return { ...chartModel(days, data.tracked_from), skipped: first };
  }, [data, mobile]);
  const h = mobile ? 140 : 200;
  return (
    <section className="an-card an-chart">
      <div className="an-sechead">
        <h3>Visits per day</h3>
        <div className="an-legend">
          <span>
            <PropDot prop="site" />
            remotion.io
          </span>
          <span>
            <PropDot prop="lp" />
            Landing pages
          </span>
          <span>
            <PropDot prop="book" />
            {mobile ? "Booking" : "Booking page"}
          </span>
          <span className="an-legend__ads">
            <span aria-hidden="true" className="an-legend__adbar" />
            {mobile ? "Ads" : "From LinkedIn ads"}
          </span>
        </div>
      </div>
      {mobile && model.skipped ? <p className="an-faint an-chart__note">Earlier days in the range weren't tracked yet.</p> : null}

      <div className="an-chart__plot">
        <div aria-hidden="true" className="an-chart__y" style={{ height: h }}>
          {model.yTicks.map((t, i) => (
            <span key={i} style={{ top: `${(i / 4) * 100}%` }}>
              {Math.round(t)}
            </span>
          ))}
        </div>
        <div className="an-chart__area" style={{ height: h }}>
          {[0, 25, 50, 75].map((p) => (
            <div key={p} aria-hidden="true" className="an-chart__grid" style={{ top: `${p}%` }} />
          ))}
          {model.untrackedPct > 0 && !mobile && (
            <div className="an-chart__untracked" style={{ width: `${model.untrackedPct}%` }}>
              <span>Not tracked yet</span>
            </div>
          )}
          <div aria-hidden="true" className="an-chart__base" />
          <div role="img" aria-label="Visits per day by property, with visits from LinkedIn ads" className="an-chart__cols">
            {model.cols.map((c) => (
              <div key={c.day} className="an-chart__col" title={c.tip}>
                <div className="an-chart__stack">
                  <div className="an-bar an-bar--site" style={{ height: `${c.pct.site}%` }} />
                  <div className="an-bar an-bar--lp" style={{ height: `${c.pct.lp}%` }} />
                  <div className="an-bar an-bar--book" style={{ height: `${c.pct.book}%` }} />
                </div>
                <div className="an-bar an-bar--ads" style={{ height: `${c.pct.ads}%` }} />
                {c.marker && !mobile && (
                  <>
                    <div aria-hidden="true" className="an-chart__marker" style={{ top: c.marker.row * 20 }} />
                    <span className="an-chart__markertext" style={{ top: c.marker.row * 20 }}>
                      {c.marker.text}
                    </span>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="an-chart__ticks">
        {model.cols.map((c) => (
          <div key={c.day} className={c.isToday ? "is-today" : ""}>
            {c.tick || ""}
          </div>
        ))}
      </div>
      <div className="an-chart__lanes">
        <div className="an-chart__lane">
          {model.cols.map((c) => (
            <div key={c.day}>{c.forms > 0 && <span className="an-lane an-lane--form" title={`${c.forms} ${c.forms === 1 ? "form" : "forms"} sent, ${c.tip.split(":")[0]}`}>{c.forms}</span>}</div>
          ))}
        </div>
        <div className="an-chart__lane">
          {model.cols.map((c) => (
            <div key={c.day}>{c.calls > 0 && <span className="an-lane an-lane--call" title={`${c.calls} ${c.calls === 1 ? "call" : "calls"} booked, ${c.tip.split(":")[0]}`}>{c.calls}</span>}</div>
          ))}
        </div>
      </div>
      <div className="an-legend an-chart__lanelegend">
        <span>
          <span aria-hidden="true" className="an-lane an-lane--form an-lane--key" />
          {mobile ? "Form" : `Forms sent (top lane) · ${data.totals.forms}`}
        </span>
        <span>
          <span aria-hidden="true" className="an-lane an-lane--call an-lane--key" />
          {mobile ? "Call booked" : `Calls booked (bottom lane) · ${data.totals.calls}`}
        </span>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------- properties -- */

function Measured({ p }) {
  const t = avgTime(p);
  return t ? <span className="an-strongish">{t}</span> : <span className="an-notyet">measuring since {MEASURED_FROM}</span>;
}

/* The conversion-rate cell; hovering says what it is made of. */
function Conv({ p, kind, strong }) {
  const c = convRate(p, kind);
  if (!c) return <span className="an-faint">–</span>;
  return (
    <span className={strong ? "an-strong" : ""} title={c.title}>
      {c.pct}
    </span>
  );
}

/* Landing page rows the page never reported on: kept, not counted (src/landing/analytics.js ran). */
function unrunNote(u) {
  if (!u || !u.visits) return null;
  const ads = u.from_ads ? `, ${u.from_ads} of them on ad links` : "";
  return `Not counted: ${u.visits} page ${u.visits === 1 ? "load" : "loads"} that never ran the page (scanners, link checkers)${ads}.`;
}

function Properties({ data, mobile }) {
  const props = data.properties;
  if (mobile) {
    return (
      <>
        <h3 className="an-phonehead">By property</h3>
        {props.map((p) => (
          <section key={p.key} className="an-card an-propcard">
            <div className="an-propcard__head">
              <span className="an-strong">
                <PropDot prop={p.kind} />
                {p.kind === "lp" ? `${p.name} landing page` : p.name}
              </span>
              <b>{p.visits}</b>
            </div>
            <div className="an-propcard__grid">
              <span>
                People <b>{p.people}</b>
              </span>
              <span>
                Ads <b className={p.from_ads ? "an-red" : ""}>{p.from_ads}</b>
              </span>
              {p.kind === "lp" && (
                <span>
                  Avg <b>{avgTime(p) || "–"}</b>
                </span>
              )}
              {p.kind !== "book" && (
                <span>
                  Forms <b>{p.forms}</b>
                </span>
              )}
              {p.kind !== "site" && (
                <span>
                  Calls <b>{p.calls}</b>
                </span>
              )}
              <span>
                Conv. <b>{(convRate(p) || {}).pct || "–"}</b>
              </span>
            </div>
            {p.kind !== "lp" && !avgTime(p) && <p className="an-notyet">Avg time: measuring since {MEASURED_FROM}</p>}
            {p.kind === "lp" && (
              <>
                <ul className="an-propcard__variants">
                  {p.variants.map((v) => (
                    <li key={v.key}>
                      ↳ variant {v.key}
                      {v.label ? ` “${v.label}”` : ""}{v.sends_to ? ` → ${v.sends_to}` : ""} · {variantNote(v, p.variants, data.range)} · <b>{v.visits}</b> · ads <b className="an-red">{v.from_ads}</b> · conv. <b>{(convRate(v, "lp") || {}).pct || "–"}</b>
                    </li>
                  ))}
                </ul>
                {unrunNote(p.unrun) && <p className="an-muted an-small">{unrunNote(p.unrun)}</p>}
                <Link className="an-ablink" to="/inbound/landing-pages?tab=analytics">
                  A/B detail · Landing pages ▸ Analytics
                </Link>
              </>
            )}
            {p.kind === "book" && <p className="an-muted an-small">{p.personal_visits} visits from personal links</p>}
          </section>
        ))}
      </>
    );
  }
  return (
    <section className="an-card an-props">
      <h3>By property</h3>
      <div className="an-scrollx">
        <table className="an-table an-table--props">
          <thead>
            <tr>
              <th scope="col">Property</th>
              <th scope="col">Visits</th>
              <th scope="col">People</th>
              <th scope="col" className="an-red">
                From ads
              </th>
              <th scope="col">Avg time</th>
              <th scope="col">Forms</th>
              <th scope="col">Calls</th>
              <th scope="col" title="Forms sent ÷ people; on the booking page, calls booked ÷ people">
                Conv. rate
              </th>
            </tr>
          </thead>
          <tbody>
            {props.map((p) => (
              <PropertyRows key={p.key} p={p} range={data.range} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PropertyRows({ p, range }) {
  const main = (
    <tr className="an-props__main">
      <th scope="row">
        <span className="an-inline">
          <PropDot prop={p.kind} />
          {p.kind === "lp" ? `${p.name} landing page` : p.name}
        </span>
      </th>
      <td className="an-strong">{p.visits}</td>
      <td>{p.people}</td>
      <td className={p.from_ads ? "an-red an-strong" : "an-faint"}>{p.from_ads}</td>
      <td>
        <Measured p={p} />
      </td>
      <td className={p.kind === "book" ? "an-faint" : ""}>{p.kind === "book" ? "–" : p.forms}</td>
      <td className={p.kind === "site" ? "an-faint" : p.kind === "book" ? "an-strong an-nowrap" : ""}>
        {p.kind === "site" ? "–" : p.kind === "book" ? `${p.calls} booked` : p.calls}
      </td>
      <td>
        <Conv p={p} strong />
      </td>
    </tr>
  );
  if (p.kind === "lp") {
    return (
      <>
        {main}
        {p.variants.map((v) => (
          <tr key={v.key} className="an-props__variant">
            <th scope="row">
              ↳ variant {v.key}
              {v.label ? ` “${v.label}”` : ""}{v.sends_to ? ` → ${v.sends_to}` : ""} <span className="an-faint">· {variantNote(v, p.variants, range)}</span>
            </th>
            <td>{v.visits}</td>
            <td>{v.people}</td>
            <td className="an-red">{v.from_ads}</td>
            <td />
            <td>{v.forms}</td>
            <td>{v.calls}</td>
            <td>
              <Conv p={v} kind="lp" />
            </td>
          </tr>
        ))}
        {unrunNote(p.unrun) && (
          <tr className="an-props__note">
            <td colSpan={8} className="an-muted">
              {unrunNote(p.unrun)}
            </td>
          </tr>
        )}
        <tr className="an-props__note">
          <td colSpan={8}>
            <Link className="an-ablink" to="/inbound/landing-pages?tab=analytics">
              A/B by ad set and variant · Landing pages ▸ Analytics
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17 17 7M7 7h10v10" />
              </svg>
            </Link>
          </td>
        </tr>
      </>
    );
  }
  if (p.kind === "book") {
    return (
      <>
        {main}
        <tr className="an-props__note">
          <td colSpan={8} className="an-muted">
            {p.personal_visits} visits from personal links, people we sent a link to by name
          </td>
        </tr>
      </>
    );
  }
  return main;
}

/* -------------------------------------------------------------- sources -- */

// A person is counted once per property: the lp_id cookie on a landing page, a
// day-salted ip hash on remotion.io and the booking page (src/analytics/overview.js).
const PEOPLE_TIP = "Different people behind the visits. On remotion.io and the booking page, someone who comes back on another day counts again.";

function Sources({ data, mobile }) {
  const rows = sourceRows(data.sources).slice(0, 10);
  return (
    <section className="an-card an-sources">
      <div className="an-sechead">
        <h3>Where visitors came from</h3>
        <span className="an-muted an-small">
          Where each visit started · <b className="an-red">ads in red</b>
        </span>
      </div>
      {rows.length === 0 && <p className="an-muted">No visits in this range.</p>}
      {mobile ? (
        <ul className="an-plainlist">
          {rows.map((s) => (
            <li key={`${s.kind}|${s.label}`} className="an-srcitem">
              <div className="an-srcitem__top">
                <span className={`an-srcitem__name an-src--${s.view.kind}`}>{s.view.text}</span>
                <b className={s.view.kind === "ad" ? "an-red" : ""}>{s.visits}</b>
              </div>
              <div className="an-srcitem__meta">
                <span className={`an-kindpill an-kindpill--${s.view.kind} an-kindpill--word`}>{s.view.kind === "direct" ? "direct · includes team + link checkers" : s.view.kind === "ad" ? "LinkedIn ad" : s.kindWord}</span>
                <span>{s.people} {s.people === 1 ? "person" : "people"}</span>
                <span>{s.to.map((t) => `${t.label} ${t.n}`).join(" · ")}</span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        rows.length > 0 && (
          <div className="an-scrollx">
            <table className="an-table an-table--sources">
              <thead>
                <tr>
                  <th scope="col">Source</th>
                  <th scope="col">Kind</th>
                  <th scope="col">Visits</th>
                  <th scope="col" className="an-right" title={PEOPLE_TIP}>
                    People
                  </th>
                  <th scope="col">Went to</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={`${s.kind}|${s.label}`} className={s.view.kind === "ad" ? "is-ad" : ""}>
                    <th scope="row" className={`an-src--${s.view.kind}`}>
                      <span className="an-ellipsis" title={s.title}>
                        {s.title}
                      </span>
                      {s.note && <span className="an-srcnote">{s.note}</span>}
                    </th>
                    <td>
                      <span className={`an-kindpill an-kindpill--${s.view.kind} an-kindpill--word`}>{s.kindWord}</span>
                    </td>
                    <td className="an-nowrap">
                      <span className="an-inline">
                        <span aria-hidden="true" className="an-hbar">
                          <span className={`an-hbar__fill an-hbar__fill--${s.view.kind}`} style={{ width: `${s.barPct}%` }} />
                          {s.broken && <span className="an-hbar__break" />}
                        </span>
                        <b className={`an-hbar__n${s.view.kind === "ad" ? " an-red" : ""}`}>{s.visits}</b>
                      </span>
                    </td>
                    <td className="an-right">{s.people}</td>
                    <td className="an-wentto">
                      {s.to.map((t) => (
                        <span key={t.key}>
                          <PropDot prop={t.key} />
                          {t.label} {t.n}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </section>
  );
}

/* ------------------------------------------------------------ campaigns -- */

function Flags({ list, more }) {
  return (
    <span className="an-flags">
      {list.map((c) => (
        <span key={c.code} className="an-flags__one" title={`${c.visits} from ${c.code}`}>
          <Country code={c.code} flagOnly />
          <span>{c.visits}</span>
        </span>
      ))}
      {more > 0 && <span className="an-faint">+{more}</span>}
    </span>
  );
}

function Campaigns({ data, mobile }) {
  const groups = campaignGroups(data.campaigns);
  const outcome = (n) => (n ? <b>{n}</b> : <span className="an-faint">–</span>);
  // No cost columns at all when the spend could not be read: never a $0.
  const priced = Boolean(data.ad_spend);
  const cost = (n, title) => (n == null ? <span className="an-faint" title={title}>–</span> : usd(n));
  const unpriced = "LinkedIn has no ad set by this name";
  const costLine = (x) =>
    [
      [x.spend, "spent"],
      [x.cost_per_visit, "/ visit"],
      [x.cost_per_form, "/ form"],
      [x.cost_per_call, "/ call"],
    ]
      .filter(([n]) => n != null)
      .map(([n, w]) => `${usd(n)} ${w}`)
      .join(" · ");
  return (
    <section className="an-card an-geo an-camp">
      <div className="an-sechead">
        <h3>By ad campaign</h3>
        <span className="an-muted an-small">{campaignSummary(data.campaigns, data.totals, data.ad_spend)}</span>
      </div>
      {groups.length === 0 && <p className="an-muted">No visits from LinkedIn ads in this range.</p>}
      {mobile ? (
        groups.map((g) => (
          <div key={g.key || "none"} className="an-campgroup">
            <div className="an-campgroup__head">
              <span className={g.key ? "an-strong" : "an-muted"}>{g.name}</span>
              <span className="an-small an-muted">
                <b>{g.visits}</b> {g.visits === 1 ? "visit" : "visits"} · {g.forms} {g.forms === 1 ? "form" : "forms"} · {g.calls} {g.calls === 1 ? "call" : "calls"}
              </span>
            </div>
            {priced && g.spend != null && <div className="an-small an-muted an-campgroup__cost">{costLine(g)}</div>}
            <ul className="an-plainlist">
              {g.adSets.map((a) => (
                <li key={a.ad_set || "untagged"} className="an-geoitem">
                  <div className="an-geoitem__top">
                    <span className="an-ellipsis an-strongish">{a.name}</span>
                    {a.visits ? <b className="an-red">{a.visits}</b> : <span className="an-faint an-nowrap">0 visits</span>}
                  </div>
                  <div className="an-geoitem__meta">
                    <span>{[["site", a.site], ["LP", a.lp], ["booking", a.book]].filter(([, n]) => n).map(([k, n]) => `${k} ${n}`).join(" · ")}</span>
                    {a.forms > 0 && (
                      <span>
                        forms <b>{a.forms}</b>
                      </span>
                    )}
                    {a.calls > 0 && (
                      <span>
                        calls <b>{a.calls}</b>
                      </span>
                    )}
                    {a.named > 0 && <span>{a.named} named</span>}
                    <Flags list={a.topCountries} more={a.moreCountries} />
                  </div>
                  {priced && a.spend != null && <div className="an-geoitem__meta an-camp__cost">{costLine(a)}</div>}
                </li>
              ))}
            </ul>
          </div>
        ))
      ) : (
        groups.length > 0 && (
          <div className="an-scrollx">
            <table className="an-table an-table--geo an-table--camp">
              <thead>
                <tr>
                  <th scope="col">Ad set</th>
                  <th scope="col">Visits</th>
                  <th scope="col" className="an-right">
                    People
                  </th>
                  <th scope="col">Went to</th>
                  <th scope="col" className="an-right">
                    Avg time
                  </th>
                  <th scope="col" className="an-right">
                    Forms
                  </th>
                  <th scope="col" className="an-right">
                    Calls
                  </th>
                  {priced && (
                    <>
                      <th scope="col" className="an-right">
                        Spend
                      </th>
                      <th scope="col" className="an-right" title="Spend divided by visits">
                        Per visit
                      </th>
                      <th scope="col" className="an-right" title="Spend divided by forms">
                        Per form
                      </th>
                      <th scope="col" className="an-right" title="Spend divided by calls">
                        Per call
                      </th>
                    </>
                  )}
                  <th scope="col" className="an-right">
                    Named
                  </th>
                  <th scope="col">Countries</th>
                </tr>
              </thead>
              {groups.map((g) => (
                <tbody key={g.key || "none"}>
                  <tr className="an-campgroup__row">
                    <th scope="rowgroup" colSpan={2}>
                      <span className={g.key ? "" : "an-muted"}>{g.key ? `Campaign group: ${g.name}` : g.name}</span>
                      <span className="an-campgroup__n">{g.visits} {g.visits === 1 ? "visit" : "visits"}</span>
                    </th>
                    <td colSpan={3} />
                    <td className="an-right">{outcome(g.forms)}</td>
                    <td className="an-right">{outcome(g.calls)}</td>
                    {priced && (
                      <>
                        <td className="an-right an-strong">{cost(g.spend)}</td>
                        <td className="an-right">{cost(g.cost_per_visit)}</td>
                        <td className="an-right">{cost(g.cost_per_form)}</td>
                        <td className="an-right">{cost(g.cost_per_call)}</td>
                      </>
                    )}
                    <td colSpan={2} />
                  </tr>
                  {g.adSets.map((a) => (
                    <tr key={a.ad_set || "untagged"}>
                      <th scope="row" className="an-camp__name">
                        <span className="an-ellipsis">{a.name}</span>
                      </th>
                      <td className="an-nowrap">
                        <span className="an-inline">
                          <span aria-hidden="true" className="an-hbar an-hbar--geo">
                            <span className="an-hbar__fill an-hbar__fill--ad" style={{ width: `${a.barPct}%` }} />
                          </span>
                          {a.visits ? <b className="an-hbar__n an-red">{a.visits}</b> : <span className="an-hbar__n an-faint">0</span>}
                        </span>
                      </td>
                      <td className="an-right">{a.people || <span className="an-faint">–</span>}</td>
                      <td className="an-wentto">
                        {[["site", a.site, "site"], ["lp", a.lp, "LP"], ["book", a.book, "booking"]]
                          .filter(([, n]) => n)
                          .map(([k, n, label]) => (
                            <span key={k}>
                              <PropDot prop={k} />
                              {label} {n}
                            </span>
                          ))}
                      </td>
                      <td className="an-right">{avgTime(a) || <span className="an-faint">–</span>}</td>
                      <td className="an-right">{outcome(a.forms)}</td>
                      <td className="an-right">{outcome(a.calls)}</td>
                      {priced && (
                        <>
                          <td className="an-right">{cost(a.spend, unpriced)}</td>
                          <td className="an-right">{cost(a.cost_per_visit, a.spend == null ? unpriced : undefined)}</td>
                          <td className="an-right">{cost(a.cost_per_form, a.spend == null ? unpriced : undefined)}</td>
                          <td className="an-right">{cost(a.cost_per_call, a.spend == null ? unpriced : undefined)}</td>
                        </>
                      )}
                      <td className="an-right">{a.named || <span className="an-faint">–</span>}</td>
                      <td>
                        <Flags list={a.topCountries} more={a.moreCountries} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        )
      )}
      <p className="an-faint an-small an-geo__note">
        A visit from an ad has LinkedIn's click id or a paid utm_medium on the link. The ad set is its utm_campaign; a set with no group tag takes the group its other visits carried{data.ad_spend ? ", else its LinkedIn group" : ""}. A form or call counts for the ad when that browser's last ad visit in the range came before it. {spendNote(data.ad_spend)}
      </p>
    </section>
  );
}

/* ------------------------------------------------------------ countries -- */

function Countries({ data, mobile }) {
  const rows = countryRows(data.countries);
  const none = rows.find((c) => !c.code);
  const note = (
    <>
      Israel includes the team when the CRM can't name them.
      {none ? ` Not recorded: visits before ${COUNTRY_FROM}, or an address with no country.` : ""}
    </>
  );
  return (
    <section className="an-card an-geo">
      <div className="an-sechead">
        <h3>By country</h3>
        <span className="an-muted an-small">{countrySummary(data.countries)} · recorded from {COUNTRY_FROM}</span>
      </div>
      {rows.length === 0 && <p className="an-muted">No visits in this range.</p>}
      {mobile ? (
        <ul className="an-plainlist">
          {rows.map((c) => (
            <li key={c.code || "none"} className={`an-geoitem${c.code ? "" : " is-none"}`}>
              <div className="an-geoitem__top">
                {c.code ? <Country code={c.code} /> : <span className="an-muted">Not recorded</span>}
                <b>{c.visits}</b>
              </div>
              <div className="an-geoitem__meta">
                {c.share != null && <span>{c.share}%</span>}
                <span>
                  ads <b className={c.from_ads ? "an-red" : ""}>{c.from_ads}</b>
                </span>
                <span>{[["site", c.site], ["LP", c.lp], ["booking", c.book]].filter(([, n]) => n).map(([k, n]) => `${k} ${n}`).join(" · ")}</span>
                {c.named > 0 && <span>{c.named} named</span>}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        rows.length > 0 && (
          <div className="an-scrollx">
            <table className="an-table an-table--geo">
              <thead>
                <tr>
                  <th scope="col">Country</th>
                  <th scope="col">Visits</th>
                  <th scope="col" className="an-right">
                    Share
                  </th>
                  <th scope="col" className="an-right">
                    People
                  </th>
                  <th scope="col" className="an-right an-red">
                    From ads
                  </th>
                  <th scope="col">Went to</th>
                  <th scope="col" className="an-right">
                    Avg time
                  </th>
                  <th scope="col" className="an-right">
                    Named
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.code || "none"} className={c.code ? "" : "is-none"}>
                    <th scope="row">{c.code ? <Country code={c.code} /> : <span className="an-muted">Not recorded</span>}</th>
                    <td className="an-nowrap">
                      <span className="an-inline">
                        <span aria-hidden="true" className="an-hbar an-hbar--geo">
                          {c.code && (
                            <span className="an-hbar__fill" style={{ width: `${c.barPct}%` }}>
                              <span className="an-hbar__ads" style={{ width: `${c.adPct}%` }} />
                            </span>
                          )}
                        </span>
                        <b className="an-hbar__n">{c.visits}</b>
                      </span>
                    </td>
                    <td className="an-right an-muted">{c.share != null ? `${c.share}%` : ""}</td>
                    <td className="an-right">{c.people}</td>
                    <td className={`an-right${c.from_ads ? " an-red an-strong" : " an-faint"}`}>{c.from_ads}</td>
                    <td className="an-wentto">
                      {[["site", c.site, "site"], ["lp", c.lp, "LP"], ["book", c.book, "booking"]]
                        .filter(([, n]) => n)
                        .map(([k, n, label]) => (
                          <span key={k}>
                            <PropDot prop={k} />
                            {label} {n}
                          </span>
                        ))}
                    </td>
                    <td className="an-right">{avgTime(c) || <span className="an-faint">–</span>}</td>
                    <td className="an-right">{c.named || <span className="an-faint">–</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
      <p className="an-faint an-small an-geo__note">
        {note} The red part of each bar is visits from LinkedIn ads.
      </p>
    </section>
  );
}

/* ---------------------------------------------------------------- pages -- */

function Pages({ data, mobile }) {
  const rows = pageRows(data.pages).slice(0, 8);
  return (
    <section className="an-card an-pages">
      <div className="an-sechead">
        <h3>remotion.io pages</h3>
        {!mobile && (
          <span className="an-legend">
            <span>
              <span aria-hidden="true" className="an-dot an-dot--visits" />
              Visits
            </span>
            <span>
              <span aria-hidden="true" className="an-dot an-dot--site" />
              Landed here first
            </span>
          </span>
        )}
      </div>
      {rows.length === 0 && <p className="an-muted">No remotion.io visits in this range.</p>}
      {mobile ? (
        <ul className="an-plainlist">
          {rows.map((p) => (
            <li key={`${p.host}${p.path}`} className="an-pageitem">
              <span className="an-strongish an-ellipsis">{p.path}</span>
              <b>{p.visits}</b>
              <span className="an-muted an-small">{p.entrances} first</span>
            </li>
          ))}
        </ul>
      ) : (
        rows.length > 0 && (
          <table className="an-table an-table--pages">
            <thead>
              <tr>
                <th scope="col">Page</th>
                <th scope="col">
                  <span className="an-sr">Bar</span>
                </th>
                <th scope="col" className="an-right">
                  Visits
                </th>
                <th scope="col" className="an-right">
                  Landed here
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={`${p.host}${p.path}`}>
                  <th scope="row" className="an-nowrap">
                    {p.path}
                    {p.signal && <span className="an-signalpage">SIGNAL PAGE</span>}
                  </th>
                  <td className="an-pages__barcell">
                    <span aria-hidden="true" className="an-pagebar" style={{ width: `${p.visitPct}%` }}>
                      <span style={{ width: `${p.landPct}%` }} />
                    </span>
                  </td>
                  <td className="an-right an-strong">{p.visits}</td>
                  <td className="an-right">{p.entrances}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </section>
  );
}

/* -------------------------------------------------------------- who came -- */

const PROP_FILTERS = [
  ["all", "All"],
  ["site", "remotion.io"],
  ["lp", "Landing pages"],
  ["book", "Booking page"],
];

function WhoCame({ range, preset, onOpen, mobile }) {
  const [prop, setProp] = useState("all");
  const [known, setKnown] = useState(false);
  const [ads, setAds] = useState(false);
  const [limit, setLimit] = useState(LOG_STEP);
  const [res, setRes] = useState(null);
  const [propMenu, setPropMenu] = useState(false);

  useEffect(() => setLimit(LOG_STEP), [range, prop, known, ads]);
  useEffect(() => {
    let gone = false;
    const q = new URLSearchParams({ from: range.from, to: range.to, limit: String(limit) });
    if (prop !== "all") q.set("prop", prop);
    if (known) q.set("known", "1");
    if (ads) q.set("ads", "1");
    api(`/analytics/sessions?${q}`)
      .then((d) => !gone && setRes(d))
      .catch(() => !gone && setRes({ sessions: [], total: 0, error: true }));
    return () => {
      gone = true;
    };
  }, [range, prop, known, ads, limit]);

  const rows = useMemo(() => logRows(res ? res.sessions : []), [res]);
  const rangeWords = preset === "custom" ? "these days" : presetTitle(preset, range).toLowerCase();

  return (
    <section className="an-card an-log">
      <div className="an-log__head">
        <div className="an-inline">
          <h3>Who came</h3>
          <span className="an-muted an-small">{mobile ? "· newest first" : "Newest first"}</span>
        </div>
        {mobile ? (
          <div className="an-chips">
            <button type="button" aria-pressed={!known && !ads} className={!known && !ads ? "is-on" : ""} onClick={() => {
                setKnown(false);
                setAds(false);
              }}>
              All
            </button>
            <button type="button" aria-pressed={known} className={known ? "is-on" : ""} onClick={() => setKnown((v) => !v)}>
              People we know
            </button>
            <button type="button" aria-pressed={ads} className={ads ? "is-on" : ""} onClick={() => setAds((v) => !v)}>
              From ads
            </button>
            <span className="an-chips__menu">
              <button type="button" aria-expanded={propMenu} className={prop !== "all" ? "is-on" : ""} onClick={() => setPropMenu((v) => !v)}>
                {prop === "all" ? "Property" : PROP_FILTERS.find((f) => f[0] === prop)[1]} ▾
              </button>
              {propMenu && (
                <span className="an-chips__pop">
                  {PROP_FILTERS.map(([k, label]) => (
                    <button
                      key={k}
                      type="button"
                      className={prop === k ? "is-on" : ""}
                      onClick={() => {
                        setProp(k);
                        setPropMenu(false);
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </span>
              )}
            </span>
          </div>
        ) : (
          <div className="an-log__filters">
            <div role="group" aria-label="Property" className="an-seg an-seg--sm">
              {PROP_FILTERS.map(([k, label]) => (
                <button key={k} type="button" aria-pressed={prop === k} className={prop === k ? "is-on" : ""} onClick={() => setProp(k)}>
                  {label}
                </button>
              ))}
            </div>
            <Toggle on={known} flip={() => setKnown((v) => !v)} label="Only people we know" />
            <Toggle on={ads} flip={() => setAds((v) => !v)} label="Only from ads" />
          </div>
        )}
      </div>

      {!mobile && (
        <div aria-hidden="true" className="an-log__cols">
          <span>When</span>
          <span>Country</span>
          <span>Who</span>
          <span>Where</span>
          <span>Came from</span>
          <span className="an-right">Stayed</span>
        </div>
      )}
      <ul className="an-log__list">
        {rows.map((r) => (
          <li key={r.id} className={`an-logrow${r.person ? " is-known" : ""}`}>
            <span className="an-logrow__when">{r.when}</span>
            <span className="an-logrow__geo">
              <Country code={r.country} />
            </span>
            <div className="an-logrow__who">
              {r.person ? (
                <button type="button" className="an-person an-person--log" onClick={() => onOpen(r.person)}>
                  <span className="an-person__name">{r.person.name}</span>
                  {r.person.company && <span className="an-person__co">· {r.person.company}</span>}
                  <StageChip chip={stageChip(r.person)} />
                </button>
              ) : (
                <span className="an-someone">Someone</span>
              )}
              {r.extraWho && <span className="an-faint an-small an-block">{r.extraWho}</span>}
            </div>
            <div className="an-logrow__where">
              <PropDot prop={r.property} />
              <span className="an-ellipsis an-strongish">{r.where}</span>
              {r.extraWhere && <span className="an-faint an-small an-nowrap">{r.extraWhere}</span>}
            </div>
            <div className="an-logrow__src">
              <SourceCell view={r.source} />
            </div>
            <span className={`an-logrow__stay is-${r.stayed.kind}`}>{r.stayed.text}</span>
          </li>
        ))}
      </ul>
      {res && rows.length === 0 && <p className="an-log__empty">No visits match these filters in {rangeWords === "today" ? "today" : `the ${rangeWords}`}.</p>}
      <div className="an-log__foot">
        <span className="an-faint an-small">Stayed is measured from {MEASURED_FROM}, the country from {COUNTRY_FROM}; earlier visits show “not measured” and “–”.</span>
        {res && res.total > res.sessions.length && (
          <button type="button" className="an-btn" onClick={() => setLimit((l) => l + 25)}>
            Older visits
          </button>
        )}
      </div>
    </section>
  );
}

function Toggle({ on, flip, label }) {
  return (
    <button type="button" aria-pressed={on} className={`an-toggle${on ? " is-on" : ""}`} onClick={flip}>
      <span aria-hidden="true" className="an-toggle__box">
        {on ? "✓" : ""}
      </span>
      {label}
    </button>
  );
}

/* ---------------------------------------------------------- side panel -- */

/*
 * A known person, opened from a name: who they are, whether they are on the
 * site this second, and their visits in the last 30 days. "Open full record"
 * swaps this for the record itself.
 */
function PersonPanel({ person, live, onClose, onFull }) {
  const [visits, setVisits] = useState(null);
  useEffect(() => {
    let gone = false;
    setVisits(null);
    const from = new Date(Date.now() - 30 * 86400000).toISOString();
    api(`/analytics/sessions?contact=${encodeURIComponent(person.contact_id)}&from=${encodeURIComponent(from)}&limit=50`)
      .then((d) => !gone && setVisits(d.sessions))
      .catch(() => !gone && setVisits([]));
    return () => {
      gone = true;
    };
  }, [person.contact_id]);
  const on = live.find((e) => e.person && String(e.person.contact_id) === String(person.contact_id));
  const [, tick] = useState(0);
  useEffect(() => {
    if (!on) return undefined;
    const t = setInterval(() => tick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [on]);
  const chip = stageChip(person);
  return (
    <aside className="an-personpanel" aria-label="Contact record">
      <div className="an-personpanel__top">
        <span className="an-eyebrow">Contact</span>
        <button type="button" aria-label="Close" className="an-iconbtn" onClick={onClose}>
          <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="an-personpanel__who">
        <h2>{person.name}</h2>
        {personLine(person, { withTitle: true }) && <span className="an-muted">{personLine(person, { withTitle: true })}</span>}
        {chip && (
          <div>
            <StageChip chip={chip} />
          </div>
        )}
      </div>
      {on && (
        <div className="an-onnow">
          <span aria-hidden="true" className="an-live__dot is-on" />
          <div>
            <b>On the site right now</b>
            <span>
              {whereLabel(on)} · came from {sourceView(on.source).text}
              {on.country ? <> · <Country code={on.country} /></> : null} · {clock(secondsSince(on.landed_at || on.since))} so far
            </span>
          </div>
        </div>
      )}
      <div>
        <h3>Visits in the last 30 days</h3>
        {visits === null && <p className="an-muted an-small">Reading…</p>}
        {visits && visits.length === 0 && <p className="an-muted an-small">None in the last 30 days.</p>}
        <ul className="an-plainlist">
          {(visits || []).map((v) => (
            <li key={v.id} className="an-pvisit">
              <span className="an-muted an-small">{whenLabel(v.started_at)}</span>
              <span className="an-strongish">
                {whereLabel(v, { short: true })}
                {v.property === "site" && v.pages.length > 1 ? ` · ${v.pages.join(" → ")}` : ""}
              </span>
              <span className="an-muted an-small an-inline">
                from {sourceView(v.source).text}
                {v.country && <Country code={v.country} />}
              </span>
              {v.did && (
                <span className="an-muted an-small">
                  {didWords(v.did).scroll}
                  {didWords(v.did).none ? " · clicked nothing" : ` · clicked ${didWords(v.did).clicks.join(", ")}`}
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
      <div className="an-personpanel__actions">
        <button type="button" className="an-btn an-btn--primary" onClick={onFull}>
          Open full record
        </button>
        <button type="button" className="an-linkbtn" onClick={onClose}>
          Close
        </button>
      </div>
    </aside>
  );
}
